import { useCallback, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Coins } from "lucide-react";
import api from "@/lib/api";

export const COIN_REFRESH_EVENT = "pm-coin-balance-refresh";

export default function CoinBalanceChip() {
  const [balanceMinor, setBalanceMinor] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();
  const timerRef = useRef(null);

  const load = useCallback(() => {
    api.get("/member/coins").then((res) => setBalanceMinor(res.data.balance_minor ?? 0)).catch(() => {});
  }, []);

  useEffect(() => {
    load();
    // Pause balance polling while hidden; resume with one refresh on visibility.
    // Guarded against duplicate intervals; timer + listeners cleaned up on unmount.
    const start = () => { if (!timerRef.current) timerRef.current = setInterval(load, 30000); };
    const stop = () => { if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; } };
    const onVis = () => { if (document.hidden) stop(); else { load(); start(); } };
    if (!document.hidden) start();
    window.addEventListener(COIN_REFRESH_EVENT, load);
    document.addEventListener("visibilitychange", onVis);
    return () => {
      stop();
      window.removeEventListener(COIN_REFRESH_EVENT, load);
      document.removeEventListener("visibilitychange", onVis);
    };
  }, [load]);

  useEffect(load, [load, location.pathname]);

  return (
    <button
      onClick={() => navigate("/member/coins")}
      className="flex items-center gap-2 rounded-full border border-[#D4AF37]/30 bg-[#D4AF37]/5 hover:bg-[#D4AF37]/15 hover:border-[#D4AF37]/50 transition-colors px-3 py-1.5"
      data-testid="member-coin-balance-chip"
      title="My Coins Wallet"
    >
      <Coins className="h-3.5 w-3.5 text-[#D4AF37]" />
      <span className="hidden md:inline text-[9px] font-mono tracking-widest uppercase text-[#64748B]">Coins</span>
      <span className="text-xs font-mono font-semibold text-[#D4AF37]" data-testid="member-coin-balance-value">
        {balanceMinor === null ? "—" : (balanceMinor / 100).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
      </span>
    </button>
  );
}
